import moment from "moment";
import React, { type CSSProperties, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";

import getCurrencySymbol from "../../utils/getCurrencySymbol";
import SafeECharts from "./SafeECharts";

// Local type definitions
interface DataPoint {
  timestamp: string | number;
  profit?: number | null;
  absoluteProfit?: number | null;
  drawdown?: number | null;
}

interface GeneralPerformanceGraphProps {
  data?: DataPoint[];
  currency?: string;
  isLoading?: boolean;
  isMinimalist?: boolean;
  style?: CSSProperties;
  className?: string;
  [key: string]: unknown;
}

const GeneralPerformanceGraph: React.FC<GeneralPerformanceGraphProps> = ({
  data = [],
  currency,
  isLoading = false,
  isMinimalist,
  style,
  className,
  ...EChartsProps
}) => {
  const { i18n, t } = useTranslation("stats", { keyPrefix: "performanceGraph" });
  const symbol = useMemo(() => getCurrencySymbol(currency), [currency]);
  // Aggregate history uses `profit`; broker snapshots and the compact card
  // projection use `absoluteProfit`. Give ECharts one explicit value dimension.
  const chartData = useMemo(() => data.map(point => ({
    timestamp: point.timestamp,
    absoluteProfit: point.absoluteProfit ?? point.profit,
    drawdown: point.drawdown,
  })), [data]);

  if (isLoading) return <Skeleton height={style?.height || 400} />;

  const showDrawdown = data?.[0]?.drawdown != null;

  const profitColor = getComputedStyle(document.documentElement).getPropertyValue("--primary").trim();
  const drawdownColor = getComputedStyle(document.documentElement).getPropertyValue("--default").trim();
  return (
    <SafeECharts
      {...EChartsProps}
      className={className}
      style={style}
      option={{
        animation: !isMinimalist,
        dataset: [
          {
            dimensions: ["timestamp", "absoluteProfit", "drawdown"],
            source: chartData,
          },
        ],
        grid: {
          left: 0,
          containLabel: true,
          bottom: 0,
          top: 15,
          right: 0,
        },
        tooltip: {
          show: !isMinimalist,
          trigger: "axis",
          axisPointer: {
            label: {
              formatter: ({ value }: { value: string | number }) =>
                moment(value).locale(i18n.language).format("DD/MM/YYYY HH:mm"),
            },
          },
        },
        xAxis: {
          type: "time",
          axisLabel: {
            show: !isMinimalist,
            inside: true,
          },
          axisTick: {
            show: !isMinimalist,
            inside: true,
          },
        },
        yAxis: [
          {
            position: "right",
            min: ({ min, max }: { min: number; max: number }) => {
              const porcentualMin = Math.floor(min - (max - min) * 0.1);
              return min < 0 ? porcentualMin : Math.max(porcentualMin, 0);
            },
            axisLabel: {
              show: !isMinimalist,
              inside: true,
              verticalAlign: "bottom",
              formatter: `{value} ${symbol}`,
            },
            splitLine: {
              show: !isMinimalist,
              lineStyle: {
                opacity: 0.35,
              },
            },
            z: 2,
          },
          {
            show: showDrawdown,
            position: "left",
            min: 0,
            max: 100,
            axisLabel: {
              show: !isMinimalist,
              inside: true,
              verticalAlign: "bottom",
              formatter: "{value} %",
            },
            splitLine: {
              show: false,
            },
            z: 1,
          },
        ],
        series: [
          {
            color: profitColor,
            type: "line",
            areaStyle: {
              opacity: 0.1,
            },
            name: t("profit", { symbol }),
            step: "end",
            showSymbol: false,
            yAxisIndex: 0,
            encode: {
              x: "timestamp",
              y: "absoluteProfit",
            },
            tooltip: {
              valueFormatter: (value: number = 0) => `${value.toFixed(2)} ${symbol}`,
            },
          },
          {
            color: drawdownColor,
            type: "line",
            areaStyle: {
              opacity: 0.1,
            },
            name: t("drawdown"),
            smooth: 0.2,
            step: false,
            showSymbol: false,
            yAxisIndex: 1,
            encode: {
              x: "timestamp",
              y: "drawdown",
            },
            tooltip: {
              show: showDrawdown,
              valueFormatter: (value: number = 0) => `${value.toFixed(2)} %`,
            },
          },
        ],
      }}
    />
  );
};

// const getPerformanceGraphDataWithBoundaries = data => {
//   if (data.length <= 0) return [];
//   const firstDataItem = { ...data[0], timestamp: data[0]?.timestamp - 1, profit: 0 }
//   const lastDataItem = { ...data[data.length - 1], timestamp: new Date().getTime() }
//
//   return [firstDataItem, ...data, lastDataItem]
// }

export default GeneralPerformanceGraph;
// export { getPerformanceGraphDataWithBoundaries }
